import React from 'react';
import { X, Mail, Phone, Calendar, Music, TrendingUp, DollarSign } from 'lucide-react';
import { Modal } from '../common/Modal';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { StatusBadge } from '../common/StatusBadge';
import { Artist } from '../../types';
import { mockSongs } from '../../data/mockData';

interface ArtistDetailsProps {
  artist: Artist | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (artist: Artist) => void;
}

export const ArtistDetails: React.FC<ArtistDetailsProps> = ({
  artist,
  isOpen,
  onClose,
  onEdit
}) => {
  if (!artist) return null;

  const artistSongs = mockSongs.filter(song => song.artistId === artist.id);
  
  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" title="">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start space-x-6">
          <img
            src={artist.image}
            alt={artist.name}
            className="w-32 h-32 rounded-xl object-cover shadow-lg"
          />
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-white font-display mb-2">
              {artist.name}
            </h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              {artist.bio}
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Joined {formatDate(artist.joinedDate)}</span>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button onClick={() => onEdit(artist)}>
                Edit Artist
              </Button>
              <Button variant="outline">
                View Songs
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="text-center p-4">
            <div className="flex items-center justify-center mb-2">
              <Music className="w-6 h-6 text-blue-400 mr-2" />
              <div className="text-2xl font-bold text-white">
                {artist.totalSongs}
              </div>
            </div>
            <div className="text-sm text-gray-400">Total Songs</div>
          </Card>
          <Card className="text-center p-4">
            <div className="flex items-center justify-center mb-2">
              <TrendingUp className="w-6 h-6 text-green-400 mr-2" />
              <div className="text-2xl font-bold text-white">
                {formatNumber(artist.totalStreams)}
              </div>
            </div>
            <div className="text-sm text-gray-400">Total Streams</div>
          </Card>
          <Card className="text-center p-4">
            <div className="flex items-center justify-center mb-2">
              <DollarSign className="w-6 h-6 text-gold-400 mr-2" />
              <div className="text-2xl font-bold text-white">
                ${artist.totalRevenue.toLocaleString()}
              </div>
            </div>
            <div className="text-sm text-gray-400">Total Revenue</div>
          </Card>
        </div>

        {/* Contact Information */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Contact Information
          </h3>
          <div className="space-y-3">
            {artist.contactInfo.email && (
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-400">Email</p>
                  <a
                    href={`mailto:${artist.contactInfo.email}`}
                    className="text-white hover:text-gold-400 transition-colors"
                  >
                    {artist.contactInfo.email}
                  </a>
                </div>
              </div>
            )}
            {artist.contactInfo.phone && (
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-400">Phone</p>
                  <a
                    href={`tel:${artist.contactInfo.phone}`}
                    className="text-white hover:text-gold-400 transition-colors"
                  >
                    {artist.contactInfo.phone}
                  </a>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Social Media Links */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Social Media
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {artist.socialLinks.instagram && (
              <a
                href={`https://instagram.com/${artist.socialLinks.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:scale-105 transition-transform"
              >
                <span className="text-2xl">📷</span>
                <div>
                  <p className="text-white font-medium">Instagram</p>
                  <p className="text-white text-sm opacity-80">{artist.socialLinks.instagram}</p>
                </div>
              </a>
            )}
            {artist.socialLinks.spotify && (
              <a
                href={`https://open.spotify.com/artist/${artist.socialLinks.spotify}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-3 bg-green-500 rounded-lg hover:scale-105 transition-transform"
              >
                <span className="text-2xl">🎵</span>
                <div>
                  <p className="text-white font-medium">Spotify</p>
                  <p className="text-white text-sm opacity-80">Artist Profile</p>
                </div>
              </a>
            )}
            {artist.socialLinks.youtube && (
              <a
                href={`https://youtube.com/c/${artist.socialLinks.youtube}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-3 bg-red-500 rounded-lg hover:scale-105 transition-transform"
              >
                <span className="text-2xl">📺</span>
                <div>
                  <p className="text-white font-medium">YouTube</p>
                  <p className="text-white text-sm opacity-80">{artist.socialLinks.youtube}</p>
                </div>
              </a>
            )}
            {artist.socialLinks.twitter && (
              <a
                href={`https://twitter.com/${artist.socialLinks.twitter}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-3 bg-blue-500 rounded-lg hover:scale-105 transition-transform"
              >
                <span className="text-2xl">🐦</span>
                <div>
                  <p className="text-white font-medium">Twitter</p>
                  <p className="text-white text-sm opacity-80">{artist.socialLinks.twitter}</p>
                </div>
              </a>
            )}
          </div>
        </Card>

        {/* Recent Songs */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Recent Songs ({artistSongs.length})
          </h3>
          <div className="space-y-3">
            {artistSongs.slice(0, 5).map((song) => (
              <div key={song.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <img
                    src={song.coverArt}
                    alt={song.title}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div>
                    <p className="font-medium text-white">{song.title}</p>
                    <p className="text-sm text-gray-400">
                      {new Date(song.releaseDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">
                      {formatNumber(song.streams)} streams
                    </p>
                    <p className="text-sm text-green-400">
                      ${song.revenue.toLocaleString()}
                    </p>
                  </div>
                  <StatusBadge status={song.status} />
                </div>
              </div>
            ))}
            
            {artistSongs.length > 5 && (
              <div className="text-center pt-2">
                <Button variant="outline" size="sm">
                  View All Songs ({artistSongs.length})
                </Button>
              </div>
            )}
            
            {artistSongs.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No songs distributed yet</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </Modal>
  );
};